/**
 * Stale-bundle nudge (Live Cleanup slice 8).
 *
 * After a deploy the served index.html points at a new hashed entry script
 * (`/assets/index-<hash>.js`). A tab left open still runs the OLD bundle and,
 * worse, will 404 when it lazy-loads a now-renamed chunk — the repeated "hard
 * refresh / sign out" ritual the May-31 walkthrough hit. Two defences:
 *
 *   1. `installChunkReloadGuard()` (called from main.tsx) — auto-reload ONCE
 *      when a dynamic import / preload fails (the chunk-404 case), throttled so
 *      it can never reload-loop.
 *   2. `<VersionWatcher/>` (mounted inside ToastProvider) — polls index.html
 *      and, when the entry-script hash changes, shows a non-dismissing
 *      "new version → Refresh" toast so the user updates on their terms.
 *
 * The Caddyfile already serves index.html with `Cache-Control: no-store`, so
 * the poll always sees the freshly deployed entry filename; this is the
 * in-app nudge that closes the loop.
 */

import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/lib/ui/button";

const ENTRY_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;

/** The entry-script filename of the CURRENTLY running bundle (from the live
 *  document). Null in dev, where Vite serves `/src/main.tsx` (no hashed entry)
 *  — so the watcher is a no-op locally. */
function currentEntryFilename(): string | null {
  if (typeof document === "undefined") return null;
  for (const el of Array.from(document.querySelectorAll("script[src]"))) {
    const src = (el as HTMLScriptElement).getAttribute("src") ?? "";
    const m = src.match(ENTRY_RE);
    if (m) return m[0].split("/").pop() ?? null;
  }
  return null;
}

/** The entry-script filename the server is serving RIGHT NOW (index.html is
 *  no-store, so this reflects the latest deploy). */
async function latestEntryFilename(): Promise<string | null> {
  try {
    const res = await fetch("/index.html", { cache: "no-store" });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(ENTRY_RE);
    return m ? (m[0].split("/").pop() ?? null) : null;
  } catch {
    return null;
  }
}

/**
 * Auto-reload once on a stale-chunk load failure. Throttled to at most one
 * reload per 30s (sessionStorage timestamp) so a genuinely-broken deploy can
 * never spin the tab in a reload loop. Idempotent — safe to call once at boot.
 */
export function installChunkReloadGuard(): void {
  if (typeof window === "undefined") return;
  const KEY = "divineiot:chunk-reload-at";
  const reloadOnce = () => {
    const last = Number(sessionStorage.getItem(KEY) || 0);
    if (Date.now() - last < 30_000) return; // reloaded recently — don't loop
    sessionStorage.setItem(KEY, String(Date.now()));
    window.location.reload();
  };
  // Vite's typed signal for a failed dynamic-import preload.
  window.addEventListener("vite:preloadError", (e) => {
    e.preventDefault();
    reloadOnce();
  });
  // Belt-and-braces: catch the raw dynamic-import error message too (older
  // chromium / non-preload paths).
  window.addEventListener("error", (e) => {
    const msg = String((e as ErrorEvent)?.message ?? "");
    if (
      /dynamically imported module|Importing a module script failed|Failed to fetch dynamically imported/i.test(
        msg,
      )
    ) {
      reloadOnce();
    }
  });
}

/**
 * Polls for a newer deployed bundle and, on mismatch, renders a clear,
 * persistent update banner with a Refresh button — prominent enough not to be
 * missed (the old corner toast was too easy to overlook). Non-blocking: it's a
 * fixed bottom-center card; clicks pass through everywhere except the card, so
 * the user can finish what they're doing and refresh on their terms. Mount once
 * near the app root.
 */
export function VersionWatcher() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const loaded = currentEntryFilename();
    if (!loaded) return; // dev / no hashed entry — nothing to watch
    let alive = true;

    const check = async () => {
      if (!alive || updateAvailable) return;
      const latest = await latestEntryFilename();
      if (!alive || !latest) return;
      if (latest !== loaded) setUpdateAvailable(true);
    };

    const interval = setInterval(check, 60_000);
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    // First check shortly after mount (catches a tab opened right before a deploy).
    const kick = setTimeout(check, 5_000);

    return () => {
      alive = false;
      clearInterval(interval);
      clearTimeout(kick);
      window.removeEventListener("focus", onFocus);
    };
  }, [updateAvailable]);

  if (!updateAvailable) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex justify-center p-4"
      role="status"
      aria-live="polite"
    >
      <div className="pointer-events-auto flex w-full max-w-lg items-center gap-3 rounded-xl border border-brand/40 bg-surface-elevated p-4 shadow-xl ring-1 ring-brand/10">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-muted text-brand">
          <RefreshCw className="size-5" />
        </div>
        <div className="flex flex-1 flex-col">
          <p className="text-sm font-semibold text-on-surface">
            A new version is available
          </p>
          <p className="text-xs text-on-surface-muted">
            Refresh to load the latest update.
          </p>
        </div>
        <Button size="sm" onClick={() => window.location.reload()}>
          <RefreshCw className="mr-1.5 size-4" />
          Refresh now
        </Button>
      </div>
    </div>
  );
}
